import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { toastr } from 'react-redux-toastr';
import { selectSystemDate } from 'store/features/featuresSlice';
import { useAppSelector } from 'store';
import {
  useGetFeaturesQuery,
  useLazyGetSystemDateQuery,
  useResetDateMutation,
  useSetDateMutation,
} from 'store/features/featuresAPI';

import { SimpleSystemTime } from './components/SimpleSystemTime';
import { SystemTimeForm } from './components/SystemTimeForm';

const CUSTOM_SYSTEM_TIME_FEATURE_NAME = 'CustomSystemTime';

export const CustomSystemTime = () => {
  const [open, setOpen] = useState(false);
  const { data: features } = useGetFeaturesQuery();
  const [fetchSystemDate] = useLazyGetSystemDateQuery();
  const [setDate, { isLoading: isSetDateLoading }] = useSetDateMutation();
  const [resetDate, { isLoading: isResetDateLoading }] = useResetDateMutation();
  const systemDate = useAppSelector(selectSystemDate);

  const isEnabled = useMemo(
    () => features?.find((x) => x.name === CUSTOM_SYSTEM_TIME_FEATURE_NAME)?.isEnabled,
    [features]
  );

  const onReset = () => {
    resetDate()
      .unwrap()
      .catch((e) => {
        toastr.error('Error', 'Date can not be changed');
      })
      .then((currentDate) => {
        toastr.success(
          'Success',
          `Current time for Schools API was reset to server time ${
            currentDate ? DateTime.fromISO(currentDate).toFormat('dd/MM/yyyy hh:mm:ss') : ''
          }`
        );
      });
  };

  const onSubmit = (systemDate: string) => {
    setDate(systemDate)
      .unwrap()
      .catch((e) => {
        toastr.error('Error', 'Date can not be changed');
      })
      .then((currentDate) => {
        toastr.success(
          'Success',
          `Current time for Schools API was reset to server time ${
            currentDate ? DateTime.fromISO(currentDate).toFormat('dd/MM/yyyy hh:mm:ss') : ''
          }`
        );
      });
  };

  useEffect(() => {
    if (isEnabled) fetchSystemDate();
  }, [isEnabled, fetchSystemDate]);

  if (!isEnabled) return null;

  return (
    <Container className={classNames({ open })}>
      {!open && <SimpleSystemTime systemDate={systemDate} onOpen={() => setOpen(true)} />}
      {open && (
        <SystemTimeForm
          systemDate={systemDate}
          isSubmitting={isSetDateLoading}
          isResetInProgress={isResetDateLoading}
          onSubmit={onSubmit}
          onReset={onReset}
          onClose={() => setOpen(false)}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  z-index: 1000;
  bottom: 0;
  left: 0;
  background: var(--bc-sui-btn-default-hover-bg);
  color: var(--bc-sui-btn-default-hover-color);
  padding: 10px;

  svg {
    fill: var(--bc-sui-btn-default-hover-color);
    height: 20px;
    display: inline-block;
    &:not(#icon-calendar) {
      cursor: pointer;
    }
  }

  .form-group {
    margin: 0;
  }

  & > p {
    padding-top: 10px;
    padding-right: 20px;
    margin-bottom: 10px;
    font-size: 16px;
  }

  svg#icon-cross {
    position: absolute;
    right: 5px;
    top: 5px;
  }
`;
